import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '../lib/supabaseClient';

const OrganizationContext = createContext({});

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
};

export const OrganizationProvider = ({ children }) => {
  const { userProfile, loading: authLoading } = useAuth();
  const [currentOrganization, setCurrentOrganization] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load user's organization from AT Portal structure
  const loadOrganization = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);

      if (!userProfile.organization_id) {
        setError('User not assigned to an organization');
        setLoading(false);
        return;
      }

      const { data: org, error: orgError } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', userProfile.organization_id)
        .single();

      if (orgError) throw orgError;

      setCurrentOrganization(org);
      setUserRole(userProfile.role);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [userProfile]);

  // Load user's organization when user profile loads
  useEffect(() => {
    if (authLoading) return;

    if (userProfile) {
      loadOrganization();
    } else {
      setCurrentOrganization(null);
      setUserRole(null);
      setLoading(false);
    }
  }, [userProfile, authLoading, loadOrganization]);

  // Refresh organization data
  const refreshOrganization = async () => {
    if (userProfile) {
      await loadOrganization();
    }
  };

  // Check if user has PulseLab access (super_admin or org_admin only)
  const hasPulseLabAccess = () => {
    return userRole === 'super_admin' || userRole === 'org_admin';
  };

  // Check if user is super admin
  const isSuperAdmin = () => userRole === 'super_admin';

  // Check if user is org admin
  const isOrgAdmin = () => userRole === 'org_admin' || userRole === 'super_admin';

  // Permission helpers (for consistency with old API)
  const hasPermission = (requiredRole) => {
    if (!userRole) return false;

    const roleHierarchy = {
      super_admin: 4,
      org_admin: 3,
      team_member: 2,
      individual: 1,
    };

    const userRoleLevel = roleHierarchy[userRole] || 0;
    const requiredRoleLevel = roleHierarchy[requiredRole] || 0;

    return userRoleLevel >= requiredRoleLevel;
  };

  const value = {
    currentOrganization,
    userRole,
    loading,
    error,
    refreshOrganization,
    hasPulseLabAccess,
    isSuperAdmin,
    isOrgAdmin,
    hasPermission,
    // Legacy compatibility (for components that still use these)
    organizations: currentOrganization ? [currentOrganization] : [],
    isOwner: isSuperAdmin,
    isAdmin: isOrgAdmin,
    canManage: isOrgAdmin,
    canCreate: isOrgAdmin,
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};

export default OrganizationContext;
